import { useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Images } from '@/assets/Images'
import { JobDescription } from '@/interfaces/jobDescription'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/vi'
import { X, Download } from 'lucide-react'
import apiClient from '@/services/api/api'
import { toast } from 'react-toastify'
import { handleError } from '@/utils/helpers'

dayjs.extend(relativeTime)
dayjs.locale('vi')

interface ReactionModalProps {
  isOpen: boolean
  onClose: () => void
  jobDescription: JobDescription | undefined
}

type FilterType = 'all' | 'heart' | 'haha'

export const ReactionModal = ({ isOpen, onClose, jobDescription }: ReactionModalProps) => {
  const reactions = jobDescription?.reactions || []
  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [isDownloading, setIsDownloading] = useState(false)

  // Group by reaction type
  const heartReactions = reactions.filter((r) => r.reactionType === 'heart')
  const hahaReactions = reactions.filter((r) => r.reactionType === 'haha')

  const formatTime = (timestamp: string) => {
    try {
      return dayjs(timestamp).fromNow()
    } catch {
      return timestamp
    }
  }

  const handleDownloadReactions = async () => {
    if (!jobDescription?.id) return

    try {
      setIsDownloading(true)
      const response = await apiClient.get(
        `/reports/jd-reactions/export-excel?jdId=${jobDescription.id}`,
        {
          responseType: 'blob',
        }
      )

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with JD role and date
      const date = new Date().toISOString().split('T')[0]
      const fileName = `reactions-${jobDescription.role.replace(/\s+/g, '-')}-${date}.xlsx`
      link.download = fileName

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Tải danh sách phản ứng thành công!')
    } catch (error) {
      console.error('Download reactions error:', error)
      handleError(error, { fallbackMessage: 'Lỗi khi tải danh sách phản ứng' })
    } finally {
      setIsDownloading(false)
    }
  }

  // Get filtered reactions based on active tab
  const getFilteredReactions = () => {
    switch (activeFilter) {
      case 'heart':
        return heartReactions
      case 'haha':
        return hahaReactions
      default:
        return reactions
    }
  }

  const filteredReactions = getFilteredReactions()

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[calc(100%-2rem)] sm:max-w-lg p-0 bg-white h-[50vh] md:h-[500px] flex flex-col overflow-hidden rounded-lg"
        showCloseButton={false}
      >
        {/* Header with tabs */}
        <div className="flex-shrink-0 bg-white border-b rounded-t-lg">
          {/* Reaction filter tabs with close button */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveFilter('all')}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeFilter === 'all'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span>Tất cả</span>
                <span className="text-xs">{reactions.length}</span>
              </button>

              {heartReactions.length > 0 && (
                <button
                  onClick={() => setActiveFilter('heart')}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeFilter === 'heart'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <img src={Images.icon_heart} alt="heart" className="w-4 h-4" />
                  <span className="text-xs">{heartReactions.length}</span>
                </button>
              )}

              {hahaReactions.length > 0 && (
                <button
                  onClick={() => setActiveFilter('haha')}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeFilter === 'haha'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <img src={Images.icon_haha} alt="haha" className="w-4 h-4" />
                  <span className="text-xs">{hahaReactions.length}</span>
                </button>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              {/* Download button */}
              <button
                onClick={handleDownloadReactions}
                disabled={isDownloading || reactions.length === 0}
                className="hover:bg-gray-100 p-1.5 transition-colors rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                title="Tải xuống danh sách phản ứng"
              >
                <Download className="w-4 h-4 text-gray-600" />
              </button>

              {/* Close button */}
              <button
                onClick={onClose}
                className="hover:bg-gray-100 p-1 transition-colors rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Reactions list */}
        <div className="flex-1 min-h-0 p-4 overflow-y-auto bg-white rounded-b-lg">
          {filteredReactions.length > 0 ? (
            <div className="space-y-2">
              {filteredReactions.map((reaction, idx) => (
                <div
                  key={idx}
                  className="hover:bg-gray-50 flex items-center justify-between p-2 transition-colors rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar with reaction badge */}
                    <div className="relative">
                      <div className="bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center w-10 h-10 font-semibold text-white rounded-full">
                        {reaction.displayName.charAt(0).toUpperCase()}
                      </div>
                      {/* Reaction badge */}
                      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white flex items-center justify-center">
                        <img
                          src={
                            reaction.reactionType === 'heart' ? Images.icon_heart : Images.icon_haha
                          }
                          alt={reaction.reactionType}
                          className="h-3.5 w-3.5"
                        />
                      </div>
                    </div>

                    {/* Name and time */}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {reaction.displayName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(reaction.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-sm text-center text-gray-500">Chưa có phản ứng nào</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
